import { motion } from 'framer-motion';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { useContact } from 'src/modules/communications/hooks/api/contacts';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import { ContactFieldsSection } from '../../molecules/contacts/contact-detail-panel/contact-fields-section';
import { ContactAddressesSection } from '../../molecules/contacts/contact-detail-panel/contact-addresses-section';
import { Typography } from '@material-tailwind/react';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { PiArrowLeft, PiXLight } from 'react-icons/pi';
import { useFunnelConversations } from '../../../hooks/api/funnels/funnel-conversations';
import { useState, useEffect, useMemo } from 'react';
import { ConversationsSection } from './conversations-section';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY } from 'src/modules/communications/utils/view-preference.util';
import { formatFullName } from 'src/modules/shared/utils/formatting/name.util';
import { TextSkeleton } from 'src/modules/shared/components/atoms/text/text-skeleton';

interface ContactDetailPanelProps {
  onClose?: () => void;
  contactId: number;
  conversationId?: number;
}

const ContactDetailPanel: React.FC<ContactDetailPanelProps> = ({
  onClose,
  contactId,
  conversationId,
}) => {
  const { t } = useI18n();
  const isSmallScreen = useMediaQuery(COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY);
  const isSplitView = !isSmallScreen && !!conversationId;
  const [selectedConversationIndex, setSelectedConversationIndex] = useState(0);

  const {
    contact,
    loading: loadingContact,
    error: contactError,
  } = useContact({ id: contactId }, !!contactId);

  const conversationsQuery = createQuery()
    .equals('contactId', contactId)
    .build();

  const {
    conversations,
    loading: loadingConversations,
    error: conversationsError,
  } = useFunnelConversations(conversationsQuery, !!contactId);

  const conversationsToken = useMemo(() => {
    return conversations.map((conv) => conv.id).join(',');
  }, [conversations]);
  const panelSubtitle = useMemo(() => {
    return formatFullName(contact?.firstName, contact?.lastName).trim();
  }, [contact?.firstName, contact?.lastName]);

  useEffect(() => {
    if (!conversationId || conversations.length === 0) return;

    const index = conversations.findIndex(
      (conversation) => conversation.id === conversationId
    );
    const foundConversation = index !== -1;
    if (foundConversation) {
      setSelectedConversationIndex(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, conversationsToken]);

  const handlePreviousConversation = () => {
    if (conversations.length === 0) return;

    setSelectedConversationIndex((currentIndex) => {
      const isFirstConversation = currentIndex === 0;
      return isFirstConversation ? conversations.length - 1 : currentIndex - 1;
    });
  };

  const handleNextConversation = () => {
    if (conversations.length === 0) return;

    setSelectedConversationIndex((currentIndex) => {
      const isLastConversation = currentIndex === conversations.length - 1;
      return isLastConversation ? 0 : currentIndex + 1;
    });
  };

  if (contactError) {
    return (
      <motion.div
        initial={{ x: 20, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 20, opacity: 0 }}
        className="h-full flex flex-col bg-white"
      >
        <ErrorState
          title={t('communications.contactPanel.error.title')}
          description={t('communications.contactPanel.error.description')}
          onAction={onClose}
          actionContent={t('communications.contactPanel.error.close')}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ x: 20, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 20, opacity: 0 }}
      className="transition-all duration-200 h-full flex flex-col bg-white"
    >
      <PanelHeader
        className={`${
          isSplitView
            ? 'min-h-[72px] px-4 py-2'
            : 'min-h-[80px] px-3 py-2 sm:px-4'
        }`}
        containerClassName="items-center gap-2 sm:gap-3"
        collapsible={false}
        actions={
          !isSplitView && !isSmallScreen && onClose ? (
            <Button
              variant="neutral"
              appearance="ghost"
              size="md"
              onClick={onClose}
              isIconOnly
              aria-label={t('communications.contactPanel.error.close')}
            >
              <PiXLight className="h-5 w-5" />
            </Button>
          ) : undefined
        }
      >
        <div className="flex items-center gap-3 min-w-0">
          {isSmallScreen && onClose && (
            <Button
              variant="neutral"
              appearance="ghost"
              size="sm"
              onClick={onClose}
              isIconOnly
              aria-label={t('communications.contactPanel.error.close')}
            >
              <PiArrowLeft className="h-5 w-5" />
            </Button>
          )}

          <div className="flex flex-col gap-0.5 min-w-0">
            <Typography className="text-lg font-medium">
              {t('communications.contactPanel.title')}
            </Typography>
            {panelSubtitle && (
              <Typography
                className={`truncate text-gray-500 ${
                  isSplitView ? 'text-xs' : 'text-sm'
                }`}
              >
                {panelSubtitle}
              </Typography>
            )}
            {!panelSubtitle && loadingContact && (
              <TextSkeleton
                variant="secondary"
                className={`rounded bg-secondary-100 ${
                  isSplitView ? 'h-3 w-20' : 'h-4 w-28'
                }`}
              />
            )}
          </div>
        </div>
      </PanelHeader>

      <Panel
        shadow="flat"
        variant="neutral"
        className="flex-1 overflow-x-hidden overflow-y-auto bg-white rounded-none"
      >
        <div className="flex min-h-full flex-col">
          <div>
            <div className="flex flex-col gap-2">
              <ConversationsSection
                conversations={conversations}
                loading={loadingConversations}
                error={conversationsError}
                selectedConversationIndex={selectedConversationIndex}
                onPreviousConversation={handlePreviousConversation}
                onNextConversation={handleNextConversation}
              />
            </div>
          </div>

          <div>
            <div className="flex flex-col gap-2">
              <ContactFieldsSection
                contact={contact}
                loading={loadingContact}
                error={contactError}
              />
            </div>
          </div>

          <div>
            <div className="flex flex-col gap-2">
              <ContactAddressesSection
                addresses={contact?.addresses ?? []}
                loading={loadingContact}
                contactId={contactId}
                error={contactError}
              />
            </div>
          </div>
        </div>
      </Panel>
    </motion.div>
  );
};

export default ContactDetailPanel;
